import {AiChatSize} from '@AiChat/hooks/useAiChatSize.types';
import type {ImageActionAvailabilityContext, ImageActionDescriptor} from './image-actions.types';
import {ImageActionId} from './image-actions.types';

export const IMAGE_ACTION_PRIORITY: ImageActionId[] = [
  ImageActionId.PUBLISH_TO_SOCIAL_MEDIA,
  ImageActionId.CHANGE_STYLE,
  ImageActionId.PUBLISH_AS_EMAIL_CAMPAIGN,
  ImageActionId.TURN_INTO_VIDEO,
  ImageActionId.DOWNLOAD,
  ImageActionId.MAKE_TEXT_EDITABLE,
  ImageActionId.OPEN_IN_EDITOR,
  ImageActionId.PROMOTE_AS_EVENT,
  ImageActionId.SAVE_TO_MY_DESIGNS,
  ImageActionId.SHARE_DESIGN,
];

const isPromotedAsPill = (context: ImageActionAvailabilityContext): boolean => {
  return context.isPrimaryPublishingOptionPromoted || context.isEventsCalendarPromotionChat;
};

const isEventsCalendarPill = (context: ImageActionAvailabilityContext): boolean => {
  return context.isEventsCalendarPromotionChat;
};

const isOnLastMessage = (context: ImageActionAvailabilityContext): boolean => {
  return context.isLastMessage;
};

const never = (): boolean => {
  return false;
};

const always = (): boolean => {
  return true;
};

const DESCRIPTORS: Record<ImageActionId, ImageActionDescriptor> = {
  [ImageActionId.CHANGE_STYLE]: {
    id: ImageActionId.CHANGE_STYLE,
    icon: 'icon-repeat',
    labelKey: 'pmwjs_change_style',
    minimumInlineSize: AiChatSize.SMALL,
    isAvailable: (context): boolean => {
      return context.isLastMessage && context.hasImage;
    },
    isInlineEligible: always,
    isPinnedInline: true,
  },
  [ImageActionId.TURN_INTO_VIDEO]: {
    id: ImageActionId.TURN_INTO_VIDEO,
    icon: 'icon-video-ai',
    labelKey: 'pmwjs_turn_into_video',
    minimumInlineSize: AiChatSize.SMALL,
    isAvailable: (context): boolean => {
      return context.isLastMessage && context.isCreateAVideoFeatureEnabled;
    },
    isInlineEligible: always,
  },
  [ImageActionId.PUBLISH_TO_SOCIAL_MEDIA]: {
    id: ImageActionId.PUBLISH_TO_SOCIAL_MEDIA,
    icon: 'icon-social-media',
    labelKey: 'pmwjs_post_on_social_media',
    minimumInlineSize: AiChatSize.SMALL,
    isAvailable: isOnLastMessage,
    isInlineEligible: isPromotedAsPill,
  },
  [ImageActionId.DOWNLOAD]: {
    id: ImageActionId.DOWNLOAD,
    icon: 'icon-download',
    labelKey: 'pmwjs_download',
    minimumInlineSize: null,
    isAvailable: isOnLastMessage,
    isInlineEligible: never,
  },
  [ImageActionId.MAKE_TEXT_EDITABLE]: {
    id: ImageActionId.MAKE_TEXT_EDITABLE,
    icon: 'icon-prints-stack',
    labelKey: 'pmwjs_editable_text',
    minimumInlineSize: null,
    isAvailable: (context): boolean => {
      return context.isLastMessage && (context.canUseEditableTextLayers || context.hasLayeredPoster);
    },
    isInlineEligible: never,
  },
  [ImageActionId.OPEN_IN_EDITOR]: {
    id: ImageActionId.OPEN_IN_EDITOR,
    icon: 'icon-image',
    labelKey: 'pmwjs_edit_as_image',
    minimumInlineSize: null,
    isAvailable: isOnLastMessage,
    isInlineEligible: never,
  },
  [ImageActionId.PUBLISH_AS_EMAIL_CAMPAIGN]: {
    id: ImageActionId.PUBLISH_AS_EMAIL_CAMPAIGN,
    icon: 'icon-envelope',
    labelKey: 'pmwjs_send_as_email',
    minimumInlineSize: AiChatSize.MEDIUM,
    isAvailable: isOnLastMessage,
    isInlineEligible: isEventsCalendarPill,
  },
  [ImageActionId.PROMOTE_AS_EVENT]: {
    id: ImageActionId.PROMOTE_AS_EVENT,
    icon: 'icon-event',
    labelKey: 'pmwjs_turn_into_event_page',
    minimumInlineSize: null,
    isAvailable: isOnLastMessage,
    isInlineEligible: never,
  },
  [ImageActionId.SAVE_TO_MY_DESIGNS]: {
    id: ImageActionId.SAVE_TO_MY_DESIGNS,
    icon: 'icon-folder-2',
    labelKey: 'pmwjs_save_to_my_designs',
    minimumInlineSize: null,
    isAvailable: isOnLastMessage,
    isInlineEligible: never,
  },
  [ImageActionId.SHARE_DESIGN]: {
    id: ImageActionId.SHARE_DESIGN,
    icon: 'icon-group-add',
    labelKey: 'pmwjs_share_with_others',
    minimumInlineSize: null,
    isAvailable: isOnLastMessage,
    isInlineEligible: never,
  },
};

export function getOrderedImageActions(): ImageActionDescriptor[] {
  return IMAGE_ACTION_PRIORITY.map((id): ImageActionDescriptor => {
    return DESCRIPTORS[id];
  });
}
